import * as React from 'react'
import { ActivityIndicator, FlatList, Linking, Text, TouchableOpacity, View, ScrollView } from 'react-native'
import styled from '@theme/styled-components'
import theme from '@theme/Theme'

import TextInput from '@components/textInput/TextInput'
import Radio from '@components/radio/Radio'
import Icon from '@components/icon/Icons'
import Tag from '@components/tag/Tag'
import ModalSearch from '@components/modal/ModalSearch'
import CollapsableCard from '@components/card/CollapsableCard'
import Animators from '@components/root/Animators'
import IncidentCard from '@components/incidentCard/IncidentCard'
import Modal from '@components/modal/Modal'

import useI18n from '@i18n/useI18n'
import useReducer from '@store/useReducer'

import * as UserStore from '@store/user/user'
import * as CommandeStore from '@store/commande/commande'
import * as IncidentStore from '@store/incident/incident'
import * as FamilleStore from '@store/famille/famille'

import api from '@api/api'

import { FormikHelpers, useFormik } from 'formik'

import sanitize from '@utils/Sanitizer'
import useBreakpoint from '@utils/useBreakpoints'
import useNavigation from '@layout/useNavigation'

import { findFamilyParents } from '../utils'
import { HOTLINE_PHONE } from '@app/config'
import { callPhoneNumber } from '@utils/userUtils'

interface GroupedCommand {
  sousFamilleId?: string
  libelleSousFamille: string
  commandes: CommandeResponse[]
}

interface Values {
  numeroCommande: string
  referenceArticleCommande: string
  numeroLigne: string
  familles: string[]
  famillesMinEditable: number
  alleeCommande?: string
  standCommande?: string
  espaceCommande?: string
  enseigneCommande?: string
  isCommandeOrganisateur?: boolean
}

interface CommandsProps {
  commands: GroupedCommand[]
  values: Values
  onItemSelected: (item: CommandeResponse) => void
}

const CardCommands = ({ commands, onItemSelected, values }: CommandsProps) => {
  const i18n = useI18n()

  const [current, setCurrent] = React.useState<string>()

  const renderCommandeHeader = (item: GroupedCommand, open: boolean) => {
    const withIncident = !!item.commandes.find((it) => it.incidentIdList.length > 0)

    return (
      <ItemHeader>
        <ItemHeaderContent>
          <ItemHeaderTitle>{item.libelleSousFamille}</ItemHeaderTitle>
        </ItemHeaderContent>

        <ItemWarning>{withIncident && <Icon name="problem" size={25} color={theme.colors.primary} />}</ItemWarning>
      </ItemHeader>
    )
  }

  const renderChoice = (item: CommandeResponse) => {
    const selected =
      item.libelleArticle === values.referenceArticleCommande &&
      item.numeroCommande === values.numeroCommande &&
      item.numeroLigne === values.numeroLigne

    return (
      <ItemChoice
        activeOpacity={0.9}
        onPress={() => onItemSelected(item)}
        key={`${item.numeroCommande}_${item.numeroLigne}`}>
        <ItemSelection>
          <Radio
            data={[
              {
                label: item.libelleArticle,
                value: item.libelleArticle,
                description: i18n.t('screens.incident.COMMAND.commandDescription', {
                  code: item.numeroCommande,
                  type: i18n.t(`screens.incident.COMMAND_SEARCH.${item.isCommandeOrganisateur ? 'orga' : 'expo'}`),
                }),
              },
            ]}
            onItemSelected={() => onItemSelected(item)}
            value={selected ? { label: item.libelleArticle, value: item.libelleArticle } : undefined}
          />
        </ItemSelection>
        <ItemWarning>
          {item.incidentIdList.length > 0 && <Icon name="problem" size={25} color={theme.colors.negate} />}
        </ItemWarning>
      </ItemChoice>
    )
  }

  const renderItem = ({ item }: { item: GroupedCommand }) => {
    return (
      <CardContainer>
        <CollapsableCard
          header={(open) => renderCommandeHeader(item, open)}
          open={current === item.sousFamilleId}
          onOpen={() => setCurrent(current === item.sousFamilleId ? undefined : item.sousFamilleId)}>
          <>{item.commandes.map(renderChoice)}</>
        </CollapsableCard>
      </CardContainer>
    )
  }

  return (
    <FlatList
      data={commands}
      renderItem={renderItem}
      keyExtractor={(item) => item.libelleSousFamille}
      ListEmptyComponent={<MissingInfo>{i18n.t('screens.incident.COMMAND.empty')}</MissingInfo>}
    />
  )
}

const Command = ({ form, onForm }: IncidentFormProps) => {
  const init: Values = React.useMemo(
    () => ({
      numeroCommande: form.numeroCommande,
      referenceArticleCommande: form.referenceArticleCommande,
      numeroLigne: form.numeroLigne,
      familles: form.familles,
      famillesMinEditable: form.famillesMinEditable,
    }),
    [form.numeroCommande, form.referenceArticleCommande, form.numeroLigne, form.familles, form.famillesMinEditable]
  )

  const i18n = useI18n()
  const user = useReducer(UserStore.store, (s) => s.user)
  const [, bp] = useBreakpoint()
  const isPhone = bp === 'phone'
  const navigation = useNavigation()

  const [manif, setManif] = React.useState<ManifestationResponse>()

  const [contactOpen, setContactOpen] = React.useState(false)
  const [search, setSearch] = React.useState('')
  const [filter, setFilter] = React.useState<string>()
  const [status, setStatus] = React.useState<'loading' | 'loaded' | 'error'>('loaded')

  const commandes = useReducer(CommandeStore.store, (s) => s.commandes)
  const incidentList = useReducer(IncidentStore.store, (s) => s.incidents)

  const familles = useReducer(FamilleStore.store, (s) => s.activities)
  const activities = React.useMemo(() => familles.filter((f) => f.enabled), [familles])

  if (!user) {
    return null
  }

  const filtered = React.useMemo(
    () =>
      commandes
        .filter(
          (c) =>
            // Filtre selon l'enseigne et la localisation
            (form.enseignes.includes(c.enseigne) || c.enseigne === form.enseigneCommande) &&
            form.espaceCommande === c.espace &&
            form.alleeCommande === c.allee &&
            form.standCommande === c.stand
        )
        .filter(
          (c) =>
            // concaténation allée+stand, numéro de commande, sous-famille, article
            sanitize(`${c.allee}${c.stand}`).includes(sanitize(search)) ||
            sanitize(c.numeroCommande).includes(sanitize(search)) ||
            sanitize(c.libelleSousFamille).includes(sanitize(search)) ||
            sanitize(c.libelleArticle).includes(sanitize(search))
        )
        .filter((c) => !filter || c.libelleSousFamille === filter),
    [commandes, search, filter, form.enseigneCommande]
  )

  const filters = React.useMemo(
    () =>
      (Array.from(new Set(filtered.map((c) => c.libelleSousFamille).filter(Boolean))) as string[]).sort(
        (a: string, b: string) => a.localeCompare(b)
      ),
    [filtered]
  )

  const items: GroupedCommand[] = React.useMemo(
    () =>
      filtered
        .reduce((acc, cur) => {
          if (!acc.find((g) => g.id === cur.sousFamilleId)) {
            acc.push({ label: cur.libelleSousFamille || '', id: cur.sousFamilleId })
          }
          return acc
        }, [] as { label: string; id?: string }[])
        .map((grp) => ({
          sousFamilleId: grp.id,
          libelleSousFamille: grp.label,
          commandes: filtered.filter((c) => c.sousFamilleId === grp.id),
        })),
    [filtered]
  )

  const onSubmit = (vals: Values, { setSubmitting }: FormikHelpers<Values>) => {
    onForm(vals).finally(() => setSubmitting(false))
  }

  const { values, setValues, handleSubmit, dirty } = useFormik<Values>({
    enableReinitialize: true,
    initialValues: init,
    onSubmit,
  })

  const onItemSelected = (command: CommandeResponse) => {
    if (command.incidentIdList.length > 0) {
      Modal.open({
        Content: () => renderAlert(command.incidentIdList),
        position: 'center',
      })
    }

    const selectedFamille = activities.find((a) => a.id === command.sousFamilleId)
    const familles = selectedFamille ? findFamilyParents(activities, selectedFamille) : []

    setValues({
      ...values,
      numeroCommande: command.numeroCommande,
      referenceArticleCommande: command.libelleArticle,
      numeroLigne: command.numeroLigne,
      enseigneCommande: command.enseigne,
      espaceCommande: command.espace,
      alleeCommande: command.allee,
      standCommande: command.stand,
      isCommandeOrganisateur: command.isCommandeOrganisateur,
      familles: familles.map((f) => f.id),
      famillesMinEditable: familles.length,
    })
  }

  React.useEffect(() => {
    if (form.siteId && form.manifestationId) {
      setStatus('loading')

      api.manifestation
        .get(form.siteId, form.manifestationId)
        .then(setManif)
        .then(() => setStatus('loaded'))
        .catch(() => setStatus('error'))
    }
  }, [form.siteId, form.manifestationId])

  React.useEffect(() => {
    if (dirty) {
      // sumbit on change
      handleSubmit()
    }
  }, [values])

  const renderAlert = (incidentIds: string[]) => {
    const commandIncidents = incidentIds
      .map((id) => incidentList.find((incident) => incident.id === id))
      .filter(Boolean)

    return (
      <AlertContainer>
        <AlertTitleContainer>
          <AlertTitle>{i18n.t('screens.incident.COMMAND.alert.title')}</AlertTitle>
          <TouchableOpacity onPress={() => Modal.close()}>
            <Icon name="close" size={25} color={theme.colors.primary} />
          </TouchableOpacity>
        </AlertTitleContainer>
        <AlertSubTitle>{i18n.t('screens.incident.COMMAND.alert.body')}</AlertSubTitle>
        <AlertBody>
          <FlatList
            data={commandIncidents}
            renderItem={renderIncidentCard}
            ListEmptyComponent={<MissingInfo>{i18n.t('screens.incident.COMMAND.empty')}</MissingInfo>}
          />
        </AlertBody>
      </AlertContainer>
    )
  }

  const renderIncidentCard = ({ item }: { item?: IncidentResponse }) => {
    if (!!item) {
      return (
        <IncidentContainer
          activeOpacity={0.9}
          onPress={() => navigation.push(`/incidentDetail/${item.id}`, { incidentId: item.id })}>
          <IncidentCard incident={item} />
        </IncidentContainer>
      )
    }
    return null
  }

  const renderInfos = () => {
    if (!manif) {
      return null
    }

    const contactName = form.enseigneNomContact || manif.nomOrganisateur
    const contactMail = form.enseigneMail || manif.mailOrganisateur
    const contactPhone = form.enseigneTelephone || undefined
    const location = `${form.alleeCommande || i18n.t('screens.incident.COMMAND_SEARCH.unknownLoc')}${
      form.standCommande || i18n.t('screens.incident.COMMAND_SEARCH.unknownSub')
    }`
    const enseigneLocation = `${form.espaceCommande || i18n.t('screens.incident.COMMAND_SEARCH.unknownSpace')} - ${
      location || i18n.t('screens.incident.COMMAND_SEARCH.unknownSub')
    }`

    if (isPhone) {
      return (
        <InfoHeader>
          <InfoContainerMobile>
            <Name>{form.enseignes.length > 0 ? form.enseignes.join(', ') : form.enseigneCommande}</Name>
            <Location>{enseigneLocation}</Location>
            <ContactHeader activeOpacity={0.9} onPress={() => setContactOpen(!contactOpen)}>
              <ContactInfo>{i18n.t('screens.incident.COMMAND.contact')}</ContactInfo>
              <Icon name={contactOpen ? 'arrow-up' : 'arrow-down'} size={25} color={theme.colors.primary} />
            </ContactHeader>
            {contactOpen && (
              <>
                <ContactLine>
                  <ContactInfo>{contactName}</ContactInfo>
                  {!!contactPhone && (
                    <IconButton activeOpacity={0.9} onPress={() => callPhoneNumber(contactPhone)}>
                      <Icon name="phone" size={25} color={theme.colors.primary} />
                    </IconButton>
                  )}
                </ContactLine>
                <ContactLine>
                  <ContactInfo>{contactMail}</ContactInfo>
                  <IconButton activeOpacity={0.9} onPress={() => Linking.openURL(`mailto:${contactMail}`)}>
                    <Icon name="mail" size={25} color={theme.colors.primary} />
                  </IconButton>
                </ContactLine>
              </>
            )}
          </InfoContainerMobile>
        </InfoHeader>
      )
    }

    return (
      <InfoContainer>
        <Line>
          <InfoContent>
            <Name>{form.enseignes.length > 0 ? form.enseignes.join(', ') : form.enseigneCommande}</Name>
          </InfoContent>
          <InfoContent>
            <EnseigneLocation>{enseigneLocation}</EnseigneLocation>
          </InfoContent>
        </Line>
        <Separator />
        <Line>
          <InfoContent>
            <Name>{contactName}</Name>
          </InfoContent>
          <InfoContent>
            <Action activeOpacity={0.9} onPress={() => Linking.openURL(`mailto:${contactMail}`)}>
              <IconContainer>
                <Icon name="mail" color={theme.colors.tertiary} size={16} />
              </IconContainer>
              <Info>{contactMail}</Info>
            </Action>

            {!!contactPhone && (
              <Action activeOpacity={0.9} onPress={() => callPhoneNumber(contactPhone)}>
                <IconContainer>
                  <Icon name="phone" color={theme.colors.tertiary} size={16} />
                </IconContainer>
                <Info>{contactPhone}</Info>
              </Action>
            )}
          </InfoContent>
        </Line>
      </InfoContainer>
    )
  }

  const renderChoice = ({ item }: { item: CommandeResponse }) => {
    const selected =
      // item.libelleArticle === values.referenceArticleCommande &&
      item.numeroCommande === values.numeroCommande && item.numeroLigne === values.numeroLigne

    return (
      <ItemChoice activeOpacity={0.9} onPress={() => onItemSelected(item)}>
        <ItemSelection>
          <Radio
            data={[
              {
                label: item.libelleArticle,
                value: item.libelleArticle,
                description: i18n.t('screens.incident.COMMAND.commandDescription', {
                  code: item.numeroCommande,
                  type: i18n.t(`screens.incident.COMMAND_SEARCH.${item.isCommandeOrganisateur ? 'orga' : 'expo'}`),
                }),
              },
            ]}
            onItemSelected={() => onItemSelected(item)}
            value={selected ? { label: item.libelleArticle, value: item.libelleArticle } : undefined}
          />
        </ItemSelection>
        <ItemWarning>
          {item.incidentIdList.length > 0 && <Icon name="problem" size={25} color={theme.colors.negate} />}
        </ItemWarning>
      </ItemChoice>
    )
  }

  const renderCommand = ({ item }: { item: GroupedCommand }) => {
    const hasIncident = !!item.commandes.find((c) => c.incidentIdList.length > 0)

    return (
      <Item>
        <ItemTitle>
          <ItemTitleText>{item.libelleSousFamille}</ItemTitleText>
          {hasIncident && (
            <ItemTitleIcon>
              <Icon name="problem" size={25} color={theme.colors.primary} />
            </ItemTitleIcon>
          )}
        </ItemTitle>
        <FlatList
          data={item.commandes}
          renderItem={renderChoice}
          keyExtractor={(item) => `${item.numeroCommande}_${item.numeroLigne}`}
          nestedScrollEnabled
        />
      </Item>
    )
  }

  if (status === 'error') {
    return (
      <CenteredContainer>
        <Animators>
          <Status>{i18n.t('screens.incident.COMMAND.error')}</Status>
        </Animators>
      </CenteredContainer>
    )
  }

  if (status === 'loading') {
    return (
      <CenteredContainer>
        <Animators>
          <ActivityIndicator color={theme.colors.primary} size="large" />
          <Status>{i18n.t('screens.incident.COMMAND.loading')}</Status>
        </Animators>
      </CenteredContainer>
    )
  }

  return (
    <MainContainer>
      {renderInfos()}
      <Indication>{i18n.t('screens.incident.COMMAND_SEARCH.indication')}</Indication>

      <Content>
        <TagLine>
          <ItemHeaderContent>
            <Title>{i18n.t('screens.incident.COMMAND.commands')}</Title>
          </ItemHeaderContent>
          <TagContainer>
            <ModalSearch
              title={i18n.t('screens.incident.COMMAND.modify')}
              subtitle={i18n.t('screens.incident.COMMAND.current', { presta: filter || '' })}
              data={filters.map((p) => ({ label: p, value: p }))}
              onItemSelected={setFilter}
              value={filter}>
              <Tag
                item={{
                  label: filter || i18n.t('screens.incident.COMMAND.work'),
                  value: false,
                }}
                onItemSelected={() => setFilter(undefined)}
                disabled={!filter}
                activeColor
                ellipsized
                selected={!!filter}
                leftIcon={!!filter ? 'close' : undefined}
              />
            </ModalSearch>
          </TagContainer>
        </TagLine>

        <SearchContainer>
          <TextInput
            value={search}
            onChange={setSearch}
            placeholder={i18n.t('screens.incident.COMMAND.search')}
            left={{ name: 'search' }}
            searchStyle={isPhone ? 'onLight' : undefined}
          />
        </SearchContainer>

        {isPhone ? (
          <CardCommands commands={items} onItemSelected={onItemSelected} values={values} />
        ) : (
          <FlatList
            data={items}
            keyExtractor={(item) => item.libelleSousFamille}
            renderItem={renderCommand}
            horizontal
            ListEmptyComponent={<MissingInfo>{i18n.t('screens.incident.COMMAND.empty')}</MissingInfo>}
          />
        )}

        <Complement>
          {i18n.t('screens.incident.COMMAND.infoStart')}
          <PhoneNumber onPress={() => callPhoneNumber(HOTLINE_PHONE)}>{` ${HOTLINE_PHONE} `}</PhoneNumber>
          {i18n.t('screens.incident.COMMAND.infoEnd')}
        </Complement>
      </Content>
    </MainContainer>
  )
}

export default Command

const MainContainer = styled(ScrollView)`
  flex: 1;
`

const CenteredContainer = styled(View)`
  flex: 1;
  align-items: center;
  justify-content: center;
`

const InfoContainer = styled(View)`
  background-color: ${(props) => props.theme.colors.backgroundSecondary};
  padding: 24px;
  padding-bottom: 10px;
`

const Line = styled(View)`
  flex-direction: row;
`

const TagLine = styled(View)`
  flex-direction: row;
  margin-bottom: 25px;
  align-items: center;
`

const Separator = styled(View)`
  margin-bottom: 20px;
  margin-top: 10px;
  height: 1px;
  background-color: ${(props) => props.theme.colors.disable};
`

const SearchContainer = styled(View)`
  margin-bottom: 24px;
`

const Content = styled(View)`
  margin: 0 24px;
  margin-bottom: 5px;
`

const Name = styled(Text)`
  ${(props) => props.theme.fonts.Headline};
  margin-bottom: 10px;
  ${(props) => props.theme.breakpoint === 'phone' && 'text-align: center;'}
`

const EnseigneLocation = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  color: ${(props) => props.theme.colors.tertiary};
`

const Title = styled(Text)`
  ${(props) => props.theme.fonts.Headline};
`

const Info = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  color: ${(props) => props.theme.colors.tertiary};
  margin-bottom: 10px;
`

const InfoContent = styled(View)`
  flex: 1;
`

const IconContainer = styled(View)`
  margin-right: 10px;
  margin-bottom: 10px;
`

const Action = styled(TouchableOpacity)`
  flex-direction: row;
  align-items: center;
`

const Item = styled(View)`
  align-self: flex-start;
  width: 265px;
  max-height: 500px;
  margin-right: 15px;
  border: 1px solid ${(props) => props.theme.colors.border};
  border-bottom-width: 0;
  background-color: ${(props) => props.theme.colors.background};
`

const ItemTitle = styled(View)`
  flex-direction: row;
  align-items: center;
  padding: 16px 24px;
  border: 0 solid ${(props) => props.theme.colors.border};
  border-bottom-width: 1px;
`

const ItemTitleText = styled(Text)`
  ${(props) => props.theme.fonts.Headline};
`

const ItemTitleIcon = styled(View)`
  margin-left: 24px;
`

const ItemChoice = styled(TouchableOpacity)`
  padding: 15px 12px;
  border: 0 solid ${(props) => props.theme.colors.border};
  ${(props) => (props.theme.breakpoint === 'phone' ? 'border-top-width: 1px;' : 'border-bottom-width: 1px;')}
  flex-direction: row;
  align-items: center;
`

const TagContainer = styled(View)`
  margin-left: 10px;
  max-width: 200px;
`

const Complement = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  color: ${(props) => props.theme.colors.tertiary};
  margin: 20px 0 5px;
`

const Indication = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  color: ${(props) => props.theme.colors.secondaryText};
  padding: 24px;
`

const ItemSelection = styled(View)`
  flex: 1;
`

const ItemWarning = styled(View)`
  margin-left: 12px;
`

const CardContainer = styled(View)`
  margin-bottom: 10px;
`

const ItemHeader = styled(View)`
  flex-direction: row;
  align-items: center;
`

const ItemHeaderContent = styled(View)`
  flex: 1;
`

const ItemHeaderTitle = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  padding: 0 24px;
`

const Status = styled(Text)`
  ${(props) => props.theme.fonts.Option};
  color: ${(props) => props.theme.colors.secondaryText};
  text-align: center;
  margin: 15px;
`
const InfoHeader = styled(View)`
  background-color: ${(props) => props.theme.colors.primary};
  margin-bottom: 16px;
`
const InfoContainerMobile = styled(View)`
  background-color: ${(props) => props.theme.colors.background};
  border-radius: 8px;
  padding-top: 24px;
  margin: 0 16px 16px;
`

const Location = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  color: ${(props) => props.theme.colors.secondaryText};
  ${(props) => props.theme.breakpoint === 'phone' && 'text-align: center;'}
`

const AlertContainer = styled(View)`
  padding: 25px;
  height: 500px;
  background-color: ${(props) => props.theme.colors.background};
`
const AlertTitleContainer = styled(View)`
  margin-bottom: 16px;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`

const AlertTitle = styled(Text)`
  ${(props) => props.theme.fonts.Headline};
`

const IncidentContainer = styled(TouchableOpacity)`
  margin-bottom: 10px;
  ${(props) => (props.theme.breakpoint === 'phone' ? `padding: 4px;` : `margin-right: 8px;`)};
`

const AlertSubTitle = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  margin-bottom: 16px;
`

const AlertBody = styled(View)`
  flex: 1;
  align-items: center;
`

const MissingInfo = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? ` padding: 23px 16px;
          background-color: ${props.theme.colors.background};
          text-align: center;
          font-size: 12px;`
      : `padding: 16px 0px;`}
`

const PhoneNumber = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  color: ${(props) => props.theme.colors.primary};
`

const ContactHeader = styled(TouchableOpacity)`
  flex-direction: row;
  align-items: center;
  padding: 12px 16px 0 0;
`

const ContactInfo = styled(Text)`
  flex: 1;
  padding: 12px 16px;
  ${(props) => props.theme.fonts.Body};
`

const ContactLine = styled(View)`
  flex-direction: row;
  align-items: center;
  border: 0 solid ${(props) => props.theme.colors.border};
  border-top-width: 1px;
`

const IconButton = styled(TouchableOpacity)`
  padding: 12px 16px;
`
